import React, { useState, useEffect } from 'react';
import { Modal, Button, Form, Alert } from 'react-bootstrap';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import { useSelector } from 'react-redux';
import axios from 'axios';
import TermsModal from '../../../components/common/modals/TermsModal';
import UserAgreementService from '../../../components/user/common/services/UserAgreementService';

function ProductMilestoneModal({ 
  show, 
  onHide, 
  productId, 
  productName, 
  description, 
  charges, 
  sellerId, 
  cart = [], 
  totalQuantity, 
  additionalData = {} 
}) {
  const [milestones, setMilestones] = useState([{ quantity: 1, description: '', dueDate: '' }]);
  const [error, setError] = useState('');
  
  // Terms modal states
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsData, setTermsData] = useState([]);
  const [loadingTerms, setLoadingTerms] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  
  const authInfo = useSelector((state) => state.auth.authInfo) || JSON.parse(localStorage.getItem("authInfo"));
  const isLoggedIn = useSelector((state) => state.auth.isLoggedIn);
  const history = useHistory();

  // Get today's date in YYYY-MM-DD format for min date
  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Initialize with default milestone
  useEffect(() => {
    if (totalQuantity === 1) {
      setMilestones([{ 
        quantity: 1, 
        description: `Delivery of ${productName}`,
        dueDate: ''
      }]);
    } else {
      setMilestones([{ 
        quantity: 1, 
        description: 'Milestone 1',
        dueDate: ''
      }]);
    }
  }, [totalQuantity, productName]);

  // Terms modal handlers
  const handleTermsClick = async (e) => {
    e.preventDefault();
    setLoadingTerms(true);
    setShowTermsModal(true);
    
    try {
      // Fetch the required documents for order placement
      const documents = await UserAgreementService.getRequiredDocuments('order_placement', 'buyer');
      console.log('Fetched documents for product order placement:', documents);
      setTermsData(documents?.data || []);
    } catch (error) {
      console.error('Error fetching terms:', error);
      toast.error('Error loading terms and conditions');
      setShowTermsModal(false);
    } finally {
      setLoadingTerms(false);
    }
  };

  const handleTermsModalAccepted = () => {
    setTermsAccepted(true);
    setShowTermsModal(false);
    toast.success('Terms and conditions accepted!');
  };

  const handleTermsModalRejected = () => {
    setShowTermsModal(false);
  };

  // Add new milestone
  const addMilestone = () => {
    const currentTotal = milestones.reduce((sum, m) => sum + parseInt(m.quantity || 0), 0);
    const remaining = totalQuantity - currentTotal;
    
    if (remaining > 0) {
      setMilestones([
        ...milestones, 
        { 
          quantity: Math.min(remaining, 1), 
          description: `Milestone ${milestones.length + 1}`,
          dueDate: ''
        }
      ]);
    }
  };

  // Remove milestone
  const removeMilestone = (index) => {
    if (milestones.length > 1) {
      const newMilestones = milestones.filter((_, i) => i !== index);
      setMilestones(newMilestones);
    }
  };

  // Update milestone
  const updateMilestone = (index, field, value) => {
    const newMilestones = [...milestones];
    newMilestones[index][field] = value;
    setMilestones(newMilestones);
  };

  // Validate milestones
  const validateMilestones = () => {
    const totalMilestoneQty = milestones.reduce((sum, m) => sum + parseInt(m.quantity || 0), 0);

    if (totalMilestoneQty !== totalQuantity) {
      setError(`Total milestone quantities (${totalMilestoneQty}) must equal cart total (${totalQuantity})`);
      return false;
    }

    for (let i = 0; i < milestones.length; i++) {
      const m = milestones[i];

      if (!m.quantity || parseInt(m.quantity) <= 0) {
        setError(`Milestone ${i + 1} must have quantity greater than 0`);
        return false;
      }

      if (!m.description.trim()) {
        setError(`Milestone ${i + 1} must have a description`);
        return false;
      }

      if (!m.dueDate) {
        setError(`Milestone ${i + 1} must have a due date`);
        return false;
      }

      if (i > 0) {
        const prevDate = new Date(milestones[i - 1].dueDate);
        const currDate = new Date(m.dueDate);
        if (currDate <= prevDate) {
          setError(`Milestone ${i + 1} due date must be after Milestone ${i}'s due date`);
          return false;
        }
      }
    }

    setError('');
    return true;
  };

  // Calculate amount per milestone
  const calculateMilestoneAmounts = () => {
    const totalAmount = parseFloat(charges);
    return milestones.map(milestone => {
      const percentage = parseInt(milestone.quantity) / totalQuantity;
      return (totalAmount * percentage).toFixed(2);
    });
  };

  const handleSubmit = async () => {
    if (!isLoggedIn) {
      toast.error('Please log in to place an order.');
      return;
    }

    if (!termsAccepted) {
      toast.error('Please accept the Terms and Conditions before placing the order.');
      return;
    }

    if (!validateMilestones()) return;

    if (!cart || cart.length === 0) {
      toast.error("Cart is empty");
      return;
    }

    try {
      const milestoneAmounts = calculateMilestoneAmounts();
      
      // Get first milestone amount for initial payment
      const firstMilestoneAmount = parseFloat(milestoneAmounts[0]) || 0;
      
      if (firstMilestoneAmount <= 0) {
        toast.error("Invalid first milestone amount");
        return;
      }

      // Format milestones according to backend requirements
      const formattedMilestones = milestones.map((milestone, index) => ({
        name: milestone.description,
        description: milestone.description,
        amount: parseFloat(milestoneAmounts[index]),
        quantity: parseInt(milestone.quantity),
        assignedTo: sellerId,
        dueDate: milestone.dueDate || null,
        status: 'pending'
      }));

      // Create project payload with proper milestones array
      const projectPayload = {
        title: productName,
        description: description,
        budget: parseFloat(charges),
        clientId: authInfo.id,
        sellerId: sellerId,
        productId: productId,
        milestones: formattedMilestones,
        paymentId: null,
        firstMilestonePaid: false
      };

      console.log('Creating project with payload:', projectPayload);

      const projectResponse = await axios.post('/user/product/create', projectPayload, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authInfo.token}`,
        }
      });

      if (!projectResponse.data?.success || !projectResponse.data?.project) {
        throw new Error('Failed to create project');
      }

      const projectId = projectResponse.data.project._id;
      const firstMilestoneId = projectResponse.data.milestones?.[0]?._id;

      // Store milestones in localStorage for checkout
      const milestoneData = {
        projectId: projectId,
        firstMilestoneId: firstMilestoneId,
        projectTitle: productName,
        projectDescription: description,
        totalBudget: parseFloat(charges),
        sellerId: sellerId,
        productId: productId,
        
        // Current milestone details (first milestone)
        currentMilestone: {
          index: 0,
          id: firstMilestoneId,
          name: milestones[0].description,
          description: milestones[0].description,
          amount: firstMilestoneAmount,
          quantity: parseInt(milestones[0].quantity),
          dueDate: milestones[0].dueDate || null
        },
        
        // All milestones for reference
        allMilestones: milestones.map((milestone, index) => ({
          index: index,
          id: projectResponse.data.milestones?.[index]?._id,
          name: milestone.description,
          description: milestone.description,
          amount: parseFloat(milestoneAmounts[index]),
          quantity: parseInt(milestone.quantity),
          dueDate: milestone.dueDate || null,
          status: index === 0 ? 'paid' : 'pending'
        })),
        
        // Create cart-like structure for checkout compatibility
        milestoneCart: [{
          id: `milestone_${projectId}_0`,
          name: `${productName} - Milestone 1: ${milestones[0].description}`,
          price: firstMilestoneAmount,
          quantity: 1,
          type: 'milestone',
          projectId: projectId,
          milestoneId: firstMilestoneId,
          milestoneIndex: 0,
          sellerId: sellerId,
          productId: productId,
          isMilestone: true,
          milestoneNumber: 1,
          dueDate: milestones[0].dueDate || null
        }],
        
        orderType: 'milestone_project',
        isFirstMilestone: true,
        remainingMilestones: milestones.length - 1
      };

      // Store milestone data in localStorage for checkout page
      localStorage.setItem('pendingMilestoneData', JSON.stringify(milestoneData));

      // Create Stripe payment intent for first milestone using existing endpoint
      const stripePayload = {
        amount: Math.round(firstMilestoneAmount * 100),
        currency: 'usd',
        metadata: {
          type: 'milestone_payment',
          projectId: projectId,
          milestoneId: firstMilestoneId,
          milestoneIndex: 0,
          clientId: authInfo.id,
          sellerId: sellerId,
          cart: JSON.stringify(milestoneData.milestoneCart)
        }
      };

      // Use existing payment intent creation endpoint
      console.log("STRIPE", stripePayload)
      const stripeResponse = await axios.post('/user/payment/create-intent', {
        ...stripePayload,
        userId: authInfo.id,
        projectData: {
          title: productName,
          description: description,
          budget: parseFloat(charges),
          sellerId: sellerId,
          productId: productId
        },
        milestones: formattedMilestones
      }, {
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${authInfo.token}`,
        }
      });

      if (!stripeResponse.data?.client_secret) {
        throw new Error('Failed to create payment intent');
      }

      // Navigate to existing checkout page with milestone data
      history.push('/orderCheckout', {
        client_secret: stripeResponse.data.client_secret,
        amount: Math.round(firstMilestoneAmount * 100),
        metadata: stripePayload.metadata,
        isMilestoneOrder: true,
        milestoneData: milestoneData,
        firstMilestoneId: firstMilestoneId
      });

      toast.success("Project created! Proceeding to checkout for first milestone.");
      onHide();

    } catch (error) {
      console.error("Error creating project and preparing checkout:", error);
      
      if (error.response?.status === 400) {
        const errorMsg = error.response?.data?.error || "Validation failed";
        toast.error(errorMsg);
      } else if (error.response?.status === 401) {
        toast.error("Authentication failed. Please login again.");
      } else if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("Failed to create project. Please try again.");
      }
    }
  };

  const milestoneAmounts = calculateMilestoneAmounts();
  const currentTotal = milestones.reduce((sum, m) => sum + parseInt(m.quantity || 0), 0);
  const totalAmount = milestoneAmounts.reduce((sum, amount) => sum + parseFloat(amount || 0), 0);
  const todayDate = getTodayDate();

  return (
    <>
      <Modal show={show} onHide={onHide} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Create Product Milestones</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="mb-3 p-3 bg-light rounded">
            <h6><strong>Product:</strong> {productName}</h6>
            <p><strong>Total Quantity:</strong> {totalQuantity}</p>
            <p><strong>Total Budget:</strong> ${charges}</p>
            <p><strong>Remaining Quantity:</strong> {totalQuantity - currentTotal}</p>
            <div className="alert alert-info mt-2">
              <small>
                <strong>Note:</strong> You'll pay for Milestone 1 (${milestoneAmounts[0] || '0.00'}) now. 
                Remaining milestones can be paid later through the project page.
              </small>
            </div>
          </div>

          {error && <Alert variant="danger">{error}</Alert>}

          {milestones.map((milestone, index) => (
            <div key={index} className="border p-3 mb-3 rounded shadow-sm">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <h6 className="mb-0">
                  Milestone {index + 1}
                  {index === 0 && <span className="badge bg-primary ms-2">Pay Now</span>}
                  {index > 0 && <span className="badge bg-secondary ms-2">Pay Later</span>}
                </h6>
                {milestones.length > 1 && (
                  <Button 
                    variant="outline-danger" 
                    size="sm"
                    onClick={() => removeMilestone(index)}
                  >
                    Remove
                  </Button>
                )}
              </div>
              <div className="row">
                <div className="col-md-2">
                  <Form.Group className="mb-2">
                    <Form.Label>Quantity</Form.Label>
                    <Form.Control
                      type="number"
                      min="1"
                      max={totalQuantity}
                      value={milestone.quantity}
                      onChange={(e) => updateMilestone(index, 'quantity', e.target.value)}
                    />
                  </Form.Group>
                </div>
                <div className="col-md-2">
                  <Form.Group className="mb-2">
                    <Form.Label>Amount</Form.Label>
                    <Form.Control
                      type="text"
                      value={`$${milestoneAmounts[index] || '0.00'}`}
                      disabled
                      className="bg-light"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group className="mb-2">
                    <Form.Label>Description</Form.Label>
                    <Form.Control
                      type="text"
                      value={milestone.description}
                      onChange={(e) => updateMilestone(index, 'description', e.target.value)}
                      placeholder="Describe this milestone deliverable"
                    />
                  </Form.Group>
                </div>
                <div className="col-md-4">
                  <Form.Group className="mb-2">
                    <Form.Label>Due Date <small className="text-muted"></small></Form.Label>
                    <Form.Control
                      type="date"
                      value={milestone.dueDate}
                      min={todayDate}
                      onChange={(e) => updateMilestone(index, 'dueDate', e.target.value)}
                      placeholder="Select due date"
                    />
                  </Form.Group>
                </div>
              </div>
              {milestone.dueDate && (
                <div className="row mt-2">
                  <div className="col-12">
                    <small className="text-info">
                      📅 Due: {new Date(milestone.dueDate).toLocaleDateString('en-US', {
                        weekday: 'long',
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </small>
                  </div>
                </div>
              )}
            </div>
          ))}

          {currentTotal < totalQuantity && (
            <Button 
              variant="outline-primary" 
              onClick={addMilestone} 
              className="mb-3 w-100"
            >
              + Add Milestone
            </Button>
          )}

          {/* Terms and Conditions Section */}
          <div className="mt-4 p-3 border rounded bg-light">
            <div className="d-flex justify-content-between align-items-center">
              <div>
                <h6 className="mb-1">Terms and Conditions</h6>
                <small className="text-danger">
                  {termsAccepted ? (
                    <span className="text-success">Terms accepted</span>
                  ) : (
                    'Please review and accept the terms before placing your order'
                  )}
                </small>
              </div>
              <Button 
                variant="outline-primary" 
                size="sm"
                onClick={handleTermsClick}
                disabled={loadingTerms}
              >
                {loadingTerms ? 'Loading...' : termsAccepted ? 'View Terms' : 'Review Terms'}
              </Button>
            </div>
          </div>

          <div className="border-top pt-3 bg-light p-3 rounded mt-3">
            <h6>Payment Summary:</h6>
            <div className="row">
              <div className="col-md-6">
                <p className="mb-1"><strong>Total Milestones:</strong> {milestones.length}</p>
                <p className="mb-1"><strong>Quantity Assigned:</strong> {currentTotal} / {totalQuantity}</p>
              </div>
              <div className="col-md-6 text-end">
                <p className="mb-1"><strong>Pay Now:</strong> <span className="text-success">${milestoneAmounts[0] || '0.00'}</span></p>
                <p className="mb-1"><strong>Pay Later:</strong> ${(totalAmount - parseFloat(milestoneAmounts[0] || 0)).toFixed(2)}</p>
                <p className="mb-1"><strong>Total Budget:</strong> ${totalAmount.toFixed(2)}</p>
              </div>
            </div>
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="outline-secondary" onClick={onHide}>
            Cancel
          </Button>
          <Button 
            className="outline-primary"
            onClick={handleSubmit}
            disabled={currentTotal !== totalQuantity || !isLoggedIn || !termsAccepted}
          >
            Proceed to Payment (${milestoneAmounts[0] || '0.00'})
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Terms Modal */}
      <TermsModal
        isVisible={showTermsModal}
        onClose={handleTermsModalRejected}
        onAccept={handleTermsModalAccepted}
        termsData={termsData}
        loading={loadingTerms}
      />
    </>
  );
}

export default ProductMilestoneModal;